/**
 * Conversation thread view. Replaces `conversation-modal.js`.
 *
 * Renders a full-screen chat-style modal showing all messages between the
 * current user and a peer plate. Live-updates via the conversation service's
 * subscribeThread API.
 */

import { createLogger } from '../../core/logger.js';
import { escape } from '../../shared/escape.js';
import { normalize as normalizePlate, format as formatPlate } from '../../shared/plate.js';
import { timeAgo } from '../../shared/date.js';
import { auth } from '../../core/firebase.js';
import * as spinner from '../../core/spinner.js';
import * as messageService from './message.service.js';
import * as conversationService from './conversation.service.js';
import * as phantomStore from '../phantom/phantom.store.js';
import * as phantomConfig from '../phantom/phantom.config.js';
import { LocalState } from '../../core/localState.js';

const log = createLogger('conversation.view');

const OVERLAY_ID = 'messagingConversationOverlay';

let stylesInjected = false;
/** @type {(() => void) | null} */
let activeUnsubscribe = null;
let currentPeerPlate = '';

/**
 * Open a conversation thread with the given peer plate.
 *
 * `explicitConvId` (optional but preferred) is the actual conversation id
 * stored under `userConversations/{uid}/{convId}` — the inbox view should
 * always pass it. Without it we fall back to deriving the convId from the
 * caller's current plate, which breaks after a plate replacement (the
 * old conversations live under the OLD convId and would otherwise show as
 * "no messages").
 *
 * @param {string} peerPlate
 * @param {string} [explicitConvId]
 */
export async function open(peerPlate, explicitConvId) {
  const plate = normalizePlate(peerPlate);
  // Admin/system conversations have no peer plate but DO have a fixed
  // explicit convId (e.g. `admin_${uid}`). Allow the view to open in that
  // case and label it "מנהל המערכת".
  const isAdmin = !plate && !!explicitConvId && explicitConvId.startsWith('admin_');
  // Phantom conversations live entirely in localStorage. Their convId is
  // `phantom_${plate}` and the messages list is read from `phantom.store`
  // rather than subscribed via the conversation service.
  const isPhantom = !!explicitConvId && explicitConvId.startsWith('phantom_');
  if (!plate && !isAdmin) return;
  currentPeerPlate = plate;

  const overlay = ensureModal();
  /** @type {HTMLElement} */ (overlay.querySelector('.conv-title')).textContent =
    isAdmin ? 'הודעה ממנהל המערכת' : `שיחה עם ${formatPlate(plate)}`;

  // Admin conversations are one-way (no admin app to read replies). Hide the
  // input bar so the user doesn't think they can reply into the void.
  // Phantom conversations DO allow reply — those replies stay in localStorage.
  const inputBar = /** @type {HTMLElement | null} */ (overlay.querySelector('.conv-input-bar'));
  if (inputBar) inputBar.style.display = isAdmin ? 'none' : '';
  const list = /** @type {HTMLElement} */ (overlay.querySelector('.conv-messages'));
  list.innerHTML = '<div class="conv-empty">טוען הודעות...</div>';
  openOverlay(overlay);

  if (activeUnsubscribe) activeUnsubscribe();

  // Phantom thread — render once from local storage. The thread is not
  // live (no other party to update it), so a single read is sufficient.
  if (isPhantom && phantomConfig.isEnabled()) {
    const rawMsgs = phantomStore.getPhantomMessages(plate);
    const messages = rawMsgs.map((m) => ({
      id: m.id,
      from: m.from === 'me' ? (auth().currentUser?.uid || 'me') : m.from,
      body: m.body,
      sentAt: m.sentAt,
      mine: m.from === 'me',
    }));
    renderMessages(list, messages);
    return;
  }

  // Mark the conversation as read once on open — unread badge clears.
  if (auth().currentUser) {
    const myPlate = LocalState.plate;
    const convId = explicitConvId || (myPlate ? messageService.conversationKey(myPlate, plate) : '');
    if (convId) void messageService.markConversationRead(convId);
  }

  // Subscribe — initial snapshot will populate the list within ms.
  activeUnsubscribe = conversationService.subscribeThread(plate, (messages) => {
    renderMessages(list, messages);
  }, explicitConvId);
}

export function close() {
  if (activeUnsubscribe) { activeUnsubscribe(); activeUnsubscribe = null; }
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay) closeOverlay(overlay);
}

// ====================================================================
// internals
// ====================================================================

function ensureModal() {
  let overlay = document.getElementById(OVERLAY_ID);
  if (overlay) return overlay;
  overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.className = 'drawer-overlay js-modal';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.innerHTML = `
    <aside class="drawer conv-drawer" role="dialog" aria-modal="true">
      <header class="drawer-header">
        <h3 class="conv-title">שיחה</h3>
        <button class="drawer-close" data-close aria-label="סגור">✕</button>
      </header>
      <div class="drawer-body conv-body">
        <div class="conv-messages" role="log" aria-live="polite"></div>
      </div>
      <div class="conv-input-bar">
        <textarea class="conv-input" placeholder="כתוב הודעה..." maxlength="500"></textarea>
        <button class="conv-send" type="button">שלח</button>
      </div>
      <footer class="drawer-footer">
        <button class="drawer-close-btn" data-close type="button">סגור</button>
      </footer>
    </aside>
  `;
  document.body.appendChild(overlay);
  injectStyles();
  overlay.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', close));
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('.conv-send')?.addEventListener('click', () => onSend(overlay));
  /** @type {HTMLTextAreaElement | null} */ (overlay.querySelector('.conv-input'))
    ?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void onSend(overlay);
      }
    });
  return overlay;
}

/** @param {HTMLElement} overlay */
async function onSend(overlay) {
  const input = /** @type {HTMLTextAreaElement | null} */ (overlay.querySelector('.conv-input'));
  const sendBtn = /** @type {HTMLButtonElement | null} */ (overlay.querySelector('.conv-send'));
  if (!input || !sendBtn) return;
  const body = input.value.trim();
  if (!body) return;
  sendBtn.disabled = true;
  const release = spinner.show('messaging.conv-send');

  // Phantom branch: this peer is a phantom plate (no real recipient on
  // the server). Append the new message to the local-only thread and
  // re-render — never reach `messageService.send` which would attempt an
  // SMS + RTDB fan-out to a non-existent uid.
  if (phantomConfig.isEnabled() && phantomStore.isPhantom(currentPeerPlate)) {
    try {
      phantomStore.addPhantomMessage(currentPeerPlate, body);
      input.value = '';
      // Re-render the messages panel from the updated local thread.
      const rawMsgs = phantomStore.getPhantomMessages(currentPeerPlate);
      const messages = rawMsgs.map((m) => ({
        id: m.id,
        from: m.from === 'me' ? (auth().currentUser?.uid || 'me') : m.from,
        body: m.body,
        sentAt: m.sentAt,
        mine: m.from === 'me',
      }));
      const list = /** @type {HTMLElement} */ (overlay.querySelector('.conv-messages'));
      renderMessages(list, messages);
    } finally {
      release();
      sendBtn.disabled = false;
    }
    return;
  }

  try {
    const result = await messageService.send({ toPlate: currentPeerPlate, body });
    if (result.status === 'ok') {
      input.value = '';
    } else {
      log.warn('send result:', result.status, result.message);
      try {
        const alertMod = await import('../../core/alert.js');
        alertMod.showToast(result.message || 'לא ניתן לשלוח כעת.', { kind: 'error' });
      } catch { /* ignore */ }
    }
  } catch (err) {
    log.error('send threw:', err);
    try {
      const alertMod = await import('../../core/alert.js');
      alertMod.showToast(/** @type {Error} */ (err).message || 'שגיאת רשת.', { kind: 'error' });
    } catch { /* ignore */ }
  } finally {
    sendBtn.disabled = false;
    release();
  }
}

/**
 * @param {HTMLElement} list
 * @param {Array<import('./conversation.service.js').Message>} messages
 */
function renderMessages(list, messages) {
  if (messages.length === 0) {
    list.innerHTML = '<div class="conv-empty">אין הודעות בשיחה זו עדיין.</div>';
    return;
  }
  list.innerHTML = messages.map(messageHtml).join('');
  list.scrollTop = list.scrollHeight;
}

/** @param {import('./conversation.service.js').Message} m */
function messageHtml(m) {
  const cls = m.mine ? 'conv-msg conv-mine' : 'conv-msg conv-peer';
  return `
    <div class="${cls}">
      <div class="conv-body-text">${escape(m.body)}</div>
      <div class="conv-meta">${escape(timeAgo(m.sentAt))}${m.smsSent ? ' · SMS' : ''}</div>
    </div>
  `;
}

function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    /* Conversation view — uses the standard drawer chrome (header / body /
     * footer) from drawer.css and only adds the chat-specific bits:
     *   - .conv-messages scrolling list inside .drawer-body
     *   - .conv-input-bar (textarea + send) between body and footer
     *   - chat bubble styling
     * No purple gradients or whatsapp greens — we use the same --brand-*
     * tokens as the rest of the app so it matches "המכונית שלי" etc. */

    /* Make the body strictly contain its messages so nothing leaks past
     * the input bar or the footer (the cause of the cut-off rows the
     * user reported). */
    #${OVERLAY_ID} .drawer-body {
      flex: 1 1 auto;
      min-height: 0;
      padding: 0;
      overflow: hidden;
      background: var(--paper-2);
    }
    #${OVERLAY_ID} .conv-messages {
      flex: 1 1 auto;
      min-height: 0;
      padding: var(--s-3);
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      display: flex;
      flex-direction: column;
      gap: var(--s-1);
    }

    /* Chat bubbles. */
    #${OVERLAY_ID} .conv-empty {
      text-align: center;
      color: var(--ink-500);
      padding: var(--s-6) var(--s-3);
    }
    #${OVERLAY_ID} .conv-msg {
      max-width: 78%;
      padding: 8px 12px;
      border-radius: var(--r-lg);
      box-shadow: var(--sh-1);
    }
    #${OVERLAY_ID} .conv-mine {
      align-self: flex-end;
      background: var(--brand-1-soft);
      color: var(--ink-900);
    }
    #${OVERLAY_ID} .conv-peer {
      align-self: flex-start;
      background: var(--paper);
      color: var(--ink-900);
    }
    #${OVERLAY_ID} .conv-body-text {
      white-space: pre-wrap;
      word-wrap: break-word;
      overflow-wrap: anywhere;
      font-size: var(--t-sm);
      line-height: var(--lh-snug);
    }
    #${OVERLAY_ID} .conv-meta {
      font-size: var(--t-2xs);
      color: var(--ink-500);
      margin-top: 4px;
      text-align: start;
    }

    /* Input bar — sits between scrolling body and the close-button footer. */
    #${OVERLAY_ID} .conv-input-bar {
      flex-shrink: 0;
      display: flex;
      gap: var(--s-2);
      align-items: flex-end;
      padding: var(--s-2) var(--s-3);
      background: var(--paper);
      border-top: 1px solid var(--line);
    }
    #${OVERLAY_ID} .conv-input {
      flex: 1 1 auto;
      min-height: 44px;
      max-height: 120px;
      padding: 10px 12px;
      border-radius: var(--r-md);
      border: 1px solid var(--line);
      background: var(--paper-2);
      color: var(--ink-900);
      font: inherit;
      font-size: var(--t-sm);
      resize: none;
      box-sizing: border-box;
      outline: none;
    }
    #${OVERLAY_ID} .conv-input:focus {
      border-color: var(--brand-1);
      box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.15);
    }
    #${OVERLAY_ID} .conv-send {
      flex-shrink: 0;
      min-width: 72px;
      height: 44px;
      padding: 0 16px;
      border: 0;
      border-radius: var(--r-md);
      background: var(--brand-grad);
      color: #ffffff;
      font-weight: var(--fw-bold);
      font-size: var(--t-md);
      cursor: pointer;
      box-shadow: var(--sh-brand);
    }
    #${OVERLAY_ID} .conv-send:hover { filter: brightness(1.05); }
    #${OVERLAY_ID} .conv-send:active { transform: translateY(1px); }
    #${OVERLAY_ID} .conv-send:disabled { opacity: 0.6; cursor: wait; }
  `;
  document.head.appendChild(style);
}

/** @param {HTMLElement} overlay */
function openOverlay(overlay) {
  overlay.setAttribute('aria-hidden', 'false');
  overlay.classList.add('open');
  overlay.style.display = 'flex';
  document.body.classList.add('no-scroll');
}

/** @param {HTMLElement} overlay */
function closeOverlay(overlay) {
  overlay.setAttribute('aria-hidden', 'true');
  overlay.classList.remove('open');
  setTimeout(() => {
    if (overlay.getAttribute('aria-hidden') === 'true') overlay.style.display = 'none';
  }, 300);
  document.body.classList.remove('no-scroll');
}
